import * as React from "react";
import {
	DropdownDivider,
	DropdownHeader,
	DropdownMenu,
	DropdownMenuPopover,
	DropdownMenuButton,
	DropdownMenuItem,
	DropdownMenuItemsLowLevel,
	DropdownMenuLink,
	ThemeCss,
} from "gatsby-interface";
import { positionRight } from "@reach/popover";
import { MdArrowDropDown } from "react-icons/md";
import { header as text } from "@modules/locales/default.js";
import {
	useTriggerZendeskWidget,
	ZendeskContext,
	ZendeskStatus,
} from "@modules/zendesk";
import { useTracker, GAEventType, BigQueryEventType } from "@modules/analytics";
import { useCurrentUser } from "@modules/auth";
import { useOrganizationBaseDetailsQuery } from "@modules/organization/queries.generated";

const menuButtonCss: ThemeCss = (theme) => ({
	background: "transparent",
	border: "none",
	cursor: "pointer",
	fontSize: theme.fontSizes[1],
	padding: 0,
	[theme.mediaQueries.phablet]: {
		padding: `revert`,
	},
});

const dropdownItemsCss: ThemeCss = () => ({
	maxHeight: `100%`,
});

const dropdownDividerCss: ThemeCss = (theme) => ({
	borderWidth: 0,
	background: theme.colors.grey[20],
});

const dropdownMenuPopoverCss: ThemeCss = () => ({
	zIndex: 1,
});

export function HelpMenu({ organizationId }) {
	const { trackBigQuery, trackGoogleAnalytics } = useTracker();
	const triggerZendeskWidget = useTriggerZendeskWidget();

	const { status, prepareForm } = React.useContext(ZendeskContext);
	const { currentUser } = useCurrentUser();

	const {
		loading: baseDataLoading,
		error: baseDataError,
		data: org,
	} = useOrganizationBaseDetailsQuery({
		variables: { id: organizationId },
		fetchPolicy: "cache-and-network",
	});

	const name = currentUser?.name;
	const email = currentUser?.email;
	const tier = org?.organizationDetails?.billing?.plan?.baseFeatures?.tier;
	const workspaceName = org?.organizationDetails?.name;

	React.useEffect(() => {
		if (status === ZendeskStatus.Ready && name && email) {
			prepareForm({
				name: {
					value: name,
				},
				email: {
					value: email,
				},
				tier: tier || 'UNKNOWN',
				workspace: workspaceName
			});
		}
	}, [name, email, status, tier, workspaceName]);

	return (
		<DropdownMenu>
			<DropdownMenuButton css={menuButtonCss}>
				Help <MdArrowDropDown />
			</DropdownMenuButton>
			<DropdownMenuPopover
				css={dropdownMenuPopoverCss}
				position={positionRight}
			>
				<DropdownMenuItemsLowLevel size="MAX_CONTENT" css={dropdownItemsCss}>
					<DropdownHeader>{text.documentation}</DropdownHeader>
					<DropdownMenuLink
						onSelect={() => {
							triggerZendeskWidget();
							trackGoogleAnalytics({
								eventType: GAEventType.Click,
								category: `Product Engagement - Dashboard`,
								label: {
									loc: `Header`,
									text: `help dropdown - go to cloud docs`,
								},
							});
						}}
						as={`a`}
						target={`_blank`}
						href={`https://support.gatsbyjs.com/hc/`}
					>
						{text.cloudDocumentation}
					</DropdownMenuLink>
					<DropdownMenuLink
						onClick={() => {
							trackBigQuery({
								eventType: BigQueryEventType.ButtonClicked,
								buttonName: text.ossDocumentation,
								uiSource: `User DropdownMenu`,
							});
							trackGoogleAnalytics({
								eventType: GAEventType.Click,
								category: `Product Engagement - Dashboard`,
								label: {
									loc: `Header`,
									text: `help dropdown - go to oss docs`,
								},
							});
						}}
						as={`a`}
						target={`_blank`}
						href={`${window.location.origin}/docs/`}
					>
						{text.ossDocumentation}
					</DropdownMenuLink>
					<DropdownDivider css={dropdownDividerCss} />
					<DropdownMenuLink
						onClick={() => {
							trackBigQuery({
								eventType: BigQueryEventType.ButtonClicked,
								buttonName: text.statusPage,
								uiSource: `User DropdownMenu`,
							});
							trackGoogleAnalytics({
								eventType: GAEventType.Click,
								category: `Product Engagement - Dashboard`,
								label: {
									loc: `Header`,
									text: `help dropdown - go to status page`,
								},
							});
						}}
						as={`a`}
						target={`_blank`}
						href={`https://status.gatsbyjs.com`}
					>
						{text.statusPage}
					</DropdownMenuLink>
					<DropdownDivider css={dropdownDividerCss} />
					{tier === "FREE" ? (
						<DropdownMenuLink
							onClick={() => {
								trackBigQuery({
									eventType: BigQueryEventType.ButtonClicked,
									buttonName: text.communityForum,
									uiSource: `User DropdownMenu`,
								});
								trackGoogleAnalytics({
									eventType: GAEventType.Click,
									category: `Product Engagement - Dashboard`,
									label: {
										loc: `Header`,
										text: `help dropdown - go to community forum`,
									},
								});
							}}
							as={`a`}
							target={`_blank`}
							href={`https://answers.netlify.com/`}
						>
							{text.communityForum}
						</DropdownMenuLink>
					) : (
						<DropdownMenuItem
							onSelect={() => {
								triggerZendeskWidget();
								trackGoogleAnalytics({
									eventType: GAEventType.Click,
									category: `Product Engagement - Dashboard`,
									label: {
										loc: `Header`,
										text: `help dropdown - customer support`,
									},
								});
							}}
						>
							Get Help
						</DropdownMenuItem>
					)}
				</DropdownMenuItemsLowLevel>
			</DropdownMenuPopover>
		</DropdownMenu>
	);
}
